import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, MessageCircle, Bell, User, ChevronDown, HelpCircle, Camera, Clock, X, Menu, ChevronRight, ArrowLeft, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Logo } from "./Logo";
import { giveawayLive } from "./GiveawayBanner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

const RECENT_KEY = "kledio_recent_searches";
const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } };
const saveRecent = (q) => {
  const list = [q, ...getRecent().filter((x) => x !== q)].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
};

function SearchBar({ compact = false, onNavigate }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sugg, setSugg] = useState({ suggestions: [], categories: [] });
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setSugg({ suggestions: [], categories: [] }); return; }
    debounceRef.current = setTimeout(() => {
      api.get("/search/suggest", { params: { q: q.trim() } }).then(({ data }) => setSugg(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const go = (term) => {
    const t = term.trim();
    if (!t) return;
    saveRecent(t);
    setOpen(false);
    onNavigate?.();
    nav(`/suche?q=${encodeURIComponent(t)}`);
  };

  const recent = getRecent();

  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={(e) => { e.preventDefault(); go(q); }} role="search">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
          <input
            data-testid={compact ? "mobile-search-input" : "header-search-input"}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={open ? "Wonach suchst du?" : "Suche nach Artikeln"}
            aria-label="Suche nach Artikeln"
            className="w-full rounded-full border border-border bg-secondary/60 pl-11 pr-11 py-2.5 text-[15px] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:bg-white transition-colors"
          />
          <button type="button" aria-label="Bildsuche (bald verfügbar)" title="Bildsuche – bald verfügbar" tabIndex={-1}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 cursor-default">
            <Camera className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </form>
      {open && (q.trim() ? (sugg.suggestions.length > 0 || sugg.categories.length > 0) : recent.length > 0) && (
        <div data-testid="search-suggestions" className="absolute top-full mt-2 w-full bg-white border border-border rounded-2xl shadow-lg py-2 z-50 overflow-hidden">
          {!q.trim() && recent.length > 0 && (
            <>
              <p className="px-4 py-1.5 text-xs font-semibold text-muted-foreground">Zuletzt gesucht</p>
              {recent.map((r) => (
                <button key={r} data-testid={`recent-search-${r}`} onClick={() => { setQ(r); go(r); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-secondary transition-colors">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" /> {r}
                </button>
              ))}
            </>
          )}
          {sugg.suggestions.map((s) => (
            <button key={s} onClick={() => { setQ(s); go(s); }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-secondary transition-colors">
              <Search className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" /> {s}
            </button>
          ))}
          {sugg.categories.map((c) => (
            <Link key={c.id} to={`/suche?category_id=${c.id}`} onClick={() => { setOpen(false); onNavigate?.(); }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-secondary transition-colors">
              <span className="text-xs font-semibold bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md">Kategorie</span> {c.name}
            </Link>
          ))}
          {sugg.members?.map((m) => (
            <Link key={m.id} to={`/profil/${m.id}`} onClick={() => { setOpen(false); onNavigate?.(); }}
              data-testid={`member-suggestion-${m.username}`}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-secondary transition-colors">
              <span className="text-xs font-semibold bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md">Mitglied</span> @{m.username}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MegaMenu({ cats, activeCat, onClose, onEnter, onLeave }) {
  const cat = activeCat;
  const subs = cats.filter((c) => c.parent_id === cat?.id);
  const groups = {};
  subs.forEach((s) => { const g = s.group || "Unterkategorien"; (groups[g] = groups[g] || []).push(s); });
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  if (!cat) return null;
  return (
    <div data-testid="katalog-mega-menu" role="menu" aria-label={`Kategorien ${cat.name}`}
      onMouseEnter={onEnter} onMouseLeave={onLeave}
      className="absolute left-0 right-0 top-full bg-white border-b border-border shadow-[0_24px_48px_-12px_rgba(0,0,0,0.14)] rounded-b-2xl z-40 menu-down before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-10 gap-y-7">
        {Object.entries(groups).map(([group, list]) => (
          <div key={group}>
            <p className="text-[11px] font-bold tracking-wide text-muted-foreground uppercase mb-2.5">{group}</p>
            {list.map((sub) => (
              <Link key={sub.id} to={`/suche?category_id=${cat.id}&subcategory_id=${sub.id}`} onClick={onClose}
                data-testid={`mega-sub-${sub.slug}`}
                className="block text-sm text-foreground/80 hover:text-accent hover:translate-x-0.5 transition-[color,transform] duration-150 py-1.5 focus-visible:ring-2 focus-visible:ring-ring rounded">
                {sub.name}
              </Link>
            ))}
            <Link to={`/suche?category_id=${cat.id}`} onClick={onClose} className="block text-sm font-semibold text-accent hover:underline pt-2">
              Alle anzeigen →
            </Link>
          </div>
        ))}
        {cat.popular_brands?.length > 0 && (
          <div>
            <p className="text-[11px] font-bold tracking-wide text-muted-foreground uppercase mb-2.5">Beliebte Marken</p>
            {cat.popular_brands.map((b) => (
              <Link key={b} to={`/suche?category_id=${cat.id}&brand=${encodeURIComponent(b)}`} onClick={onClose}
                className="block text-sm text-foreground/80 hover:text-accent transition-colors py-1">{b}</Link>
            ))}
          </div>
        )}
        {subs.length === 0 && (
          <Link to={`/suche?category_id=${cat.id}`} onClick={onClose} className="text-sm font-semibold text-accent hover:underline col-span-2">
            Alle Artikel in {cat.name} anzeigen →
          </Link>
        )}
      </div>
    </div>
  );
}

function MobileCatalog({ cats, onClose }) {
  const nav = useNavigate();
  const [parent, setParent] = useState(null);
  const parents = cats.filter((c) => !c.parent_id);
  const subs = parent ? cats.filter((c) => c.parent_id === parent.id) : [];
  const groups = {};
  subs.forEach((s) => { const g = s.group || "Unterkategorien"; (groups[g] = groups[g] || []).push(s); });
  const go = (url) => { onClose(); nav(url); };
  const row = "w-full flex items-center justify-between px-4 py-3.5 text-[15px] text-left border-b border-border/60 active:bg-secondary transition-colors";
  return (
    <div className="fixed inset-0 z-[70] bg-white flex flex-col md:hidden" data-testid="mobile-catalog" role="dialog" aria-label="Katalog">
      <div className="flex items-center justify-between px-3 h-14 border-b border-border shrink-0">
        {parent ? (
          <button data-testid="mobile-catalog-back" onClick={() => setParent(null)} className="flex items-center gap-1.5 text-[15px] font-semibold p-2 -ml-2">
            <ArrowLeft className="w-5 h-5" /> Kategorien
          </button>
        ) : (
          <span className="text-base font-bold px-1">Katalog</span>
        )}
        <button data-testid="mobile-catalog-close" onClick={onClose} aria-label="Schließen" className="p-2.5"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        {!parent ? parents.map((c) => (
          <button key={c.id} data-testid={`mobile-cat-${c.slug}`} onClick={() => setParent(c)} className={row}>
            <span className="font-medium">{c.name}</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        )) : (
          <>
            <p className="px-4 pt-4 pb-1 text-lg font-bold">{parent.name}</p>
            <button data-testid="mobile-cat-all" onClick={() => go(`/suche?category_id=${parent.id}`)} className={`${row} text-accent font-semibold`}>
              Alle in {parent.name} anzeigen
            </button>
            {Object.entries(groups).map(([g, list]) => (
              <div key={g}>
                <p className="px-4 pt-4 pb-1 text-[11px] font-bold tracking-wide text-muted-foreground uppercase">{g}</p>
                {list.map((s) => (
                  <button key={s.id} data-testid={`mobile-sub-${s.slug}`} onClick={() => go(`/suche?category_id=${parent.id}&subcategory_id=${s.id}`)} className={row}>
                    {s.name}
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export const Header = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [megaOpen, setMegaOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const hoverTimer = useRef(null);
  const [unread, setUnread] = useState(0);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileCatalogOpen, setMobileCatalogOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = () => api.get("/notifications").then(({ data }) => active && setUnread(data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    const close = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setActiveCat(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const isStaff = user && ["SUPPORT", "MODERATOR", "ADMIN", "SUPER_ADMIN"].includes(user.role);
  const isSeller = user && (user.role !== "BUYER" || user.seller?.stripe_account_id);
  const parents = cats.filter((c) => !c.parent_id);
  const visibleCats = parents.slice(0, 10);
  const moreCats = parents.slice(10);
  const iconBtn = "relative p-2.5 rounded-full hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <header ref={headerRef} className="sticky top-0 z-40 bg-white border-b border-border">
      {/* ===== Row 1 — Desktop ===== */}
      <div className="hidden md:flex max-w-7xl mx-auto px-4 sm:px-6 h-[68px] items-center gap-4">
        <Logo />
        <button data-testid="katalog-button" aria-expanded={!!activeCat} aria-haspopup="menu"
          onClick={() => setActiveCat(activeCat ? null : parents[0])}
          className={`flex items-center gap-1 text-[15px] font-medium px-3 py-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${activeCat ? "bg-secondary text-primary" : "hover:bg-secondary"}`}>
          Katalog <ChevronDown className={`w-4 h-4 transition-transform ${activeCat ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        <div className="flex-1 max-w-2xl"><SearchBar /></div>
        <div className="flex items-center gap-1 ml-auto">
          {user ? (
            <>
              <Link to="/favoriten" data-testid="nav-favorites" className={iconBtn} title="Favoriten" aria-label="Favoriten"><Heart className="w-5 h-5" /></Link>
              <Link to="/nachrichten" data-testid="nav-messages" className={iconBtn} title="Nachrichten" aria-label="Nachrichten"><MessageCircle className="w-5 h-5" /></Link>
              <Link to="/benachrichtigungen" data-testid="nav-notifications" className={iconBtn} title="Benachrichtigungen" aria-label="Benachrichtigungen">
                <Bell className="w-5 h-5" />
                {unread > 0 && <span data-testid="unread-badge" className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unread}</span>}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-account-menu" className={iconBtn} aria-label="Profil-Menü"><User className="w-5 h-5" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2"><p className="text-sm font-semibold">{user.username}</p><p className="text-xs text-muted-foreground">{user.email}</p></div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => nav(`/profil/${user.id}`)}>Mein Profil</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-my-items" onClick={() => nav(`/suche?seller_id=${user.id}`)}>Meine Artikel</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-sales" onClick={() => nav("/bestellungen?tab=verkaeufe")}>Verkäufe</DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => nav("/bestellungen")}>Käufe</DropdownMenuItem>
                  {isSeller && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="menu-seller" onClick={() => nav("/verkaeufer")}>Verkäuferbereich</DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-payouts" onClick={() => nav("/geldbeutel")}>Geldbeutel</DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-settings" onClick={() => nav("/konto")}>Einstellungen</DropdownMenuItem>
                  {isStaff && <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")}>Admin</DropdownMenuItem>}
                  <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); toast("Du wurdest abgemeldet."); nav("/"); }}>Abmelden</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/registrieren" data-testid="nav-register" className="text-[15px] font-medium px-3 py-2 rounded-lg hover:bg-secondary transition-colors">Registrieren</Link>
              <Link to="/anmelden" data-testid="nav-login" className="text-[15px] font-medium px-3 py-2 rounded-lg hover:bg-secondary transition-colors">Einloggen</Link>
            </>
          )}
          <Link to={user ? "/verkaufen" : "/anmelden"} data-testid="nav-sell-button"
            className="ml-2 inline-flex items-center gap-1.5 bg-accent text-accent-foreground text-[15px] font-semibold px-5 py-2.5 rounded-full hover:opacity-90 active:scale-95 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            Artikel verkaufen
          </Link>
          <Link to="/hilfe" data-testid="nav-help" className={iconBtn} title="Hilfe & Support" aria-label="Hilfe und Support"><HelpCircle className="w-5 h-5" /></Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-language" aria-label="Sprache wählen" className="flex items-center gap-0.5 text-sm font-semibold px-2.5 py-2 rounded-lg hover:bg-secondary transition-colors">
                DE <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="lang-de">Deutsch ✓</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ===== Row 1 — Mobile ===== */}
      <div className="md:hidden flex items-center gap-1 px-3 h-14">
        <button data-testid="mobile-catalog-btn" aria-label="Katalog öffnen" onClick={() => setMobileCatalogOpen(true)} className={`${iconBtn} -ml-1`}>
          <Menu className="w-5 h-5" />
        </button>
        <Logo />
        <div className="ml-auto flex items-center">
          <button data-testid="mobile-search-toggle" aria-label="Suche öffnen" onClick={() => setMobileSearchOpen(!mobileSearchOpen)} className={iconBtn}>
            {mobileSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </button>
          <Link to={user ? "/favoriten" : "/anmelden"} aria-label="Favoriten" className={iconBtn}><Heart className="w-5 h-5" /></Link>
          <Link to={user ? "/benachrichtigungen" : "/anmelden"} aria-label="Benachrichtigungen" className={iconBtn}>
            <Bell className="w-5 h-5" />
            {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unread}</span>}
          </Link>
        </div>
      </div>
      {mobileSearchOpen && (
        <div className="md:hidden px-3 pb-3">
          <SearchBar compact onNavigate={() => setMobileSearchOpen(false)} />
        </div>
      )}

      {/* ===== Row 2 — Categories ===== */}
      <nav aria-label="Kategorien" className="bg-white border-t border-border hidden md:block"
        onMouseLeave={() => { hoverTimer.current = setTimeout(() => { setActiveCat(null); setMegaOpen(false); }, 250); }}
        onMouseEnter={() => clearTimeout(hoverTimer.current)}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-12 flex items-center gap-1" data-testid="category-nav-row">
          {visibleCats.map((c) => (
            <button key={c.id} data-testid={`catnav-${c.slug}`} type="button"
              aria-expanded={activeCat?.id === c.id} aria-haspopup="menu"
              onMouseEnter={() => { clearTimeout(hoverTimer.current); setActiveCat(c); setMegaOpen(false); }}
              onClick={() => setActiveCat(activeCat?.id === c.id ? null : c)}
              className={`relative text-sm font-medium whitespace-nowrap px-3 h-12 flex items-center rounded-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring after:content-[''] after:absolute after:left-3 after:right-3 after:bottom-0 after:h-[2.5px] after:bg-accent after:rounded-full after:origin-left after:transition-transform after:duration-200 ${activeCat?.id === c.id ? "text-primary after:scale-x-100" : "text-foreground/80 hover:text-primary after:scale-x-0"}`}>
              {c.name}
            </button>
          ))}
          {moreCats.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="catnav-more" className="text-sm font-medium whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-secondary transition-colors flex items-center gap-1">
                  Mehr <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {moreCats.map((c) => (
                  <DropdownMenuItem key={c.id} data-testid={`catnav-more-${c.slug}`} onClick={() => nav(`/suche?category_id=${c.id}`)}>{c.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {giveawayLive() && (
            <Link to="/gewinnspiel" data-testid="catnav-gewinnspiel"
              className="ml-auto inline-flex items-center gap-1.5 text-sm font-bold text-accent whitespace-nowrap px-3 py-1.5 rounded-full hover:bg-accent/10 transition-colors">
              <Gift className="w-4 h-4" /> Gewinnspiel
            </Link>
          )}
        </div>
      </nav>
      <nav aria-label="Kategorien mobil" className="bg-white border-t border-border md:hidden">
        <div className="px-3 h-11 flex items-center gap-1 overflow-x-auto no-scrollbar">
          {giveawayLive() && (
            <Link to="/gewinnspiel" data-testid="catnav-gewinnspiel-mobile"
              className="inline-flex items-center gap-1 text-sm font-bold text-accent whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-accent/10">
              <Gift className="w-3.5 h-3.5" /> Gewinnspiel
            </Link>
          )}
          {visibleCats.concat(moreCats).map((c) => (
            <Link key={c.id} to={`/suche?category_id=${c.id}`}
              className="text-sm text-foreground/80 font-medium whitespace-nowrap px-3 py-1.5 rounded-lg hover:bg-secondary">
              {c.name}
            </Link>
          ))}
        </div>
      </nav>

      {activeCat && <MegaMenu cats={cats} activeCat={activeCat} onClose={() => setActiveCat(null)}
        onEnter={() => clearTimeout(hoverTimer.current)}
        onLeave={() => { hoverTimer.current = setTimeout(() => setActiveCat(null), 250); }} />}
      {mobileCatalogOpen && <MobileCatalog cats={cats} onClose={() => setMobileCatalogOpen(false)} />}
    </header>
  );
};
